import React from 'react';
import styled from 'styled-components';
import { Modal, InputTextArea, Button, Icon } from 'components';
import DocumentsContext from './DocumentsContext';

type RejectionModalProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  editingReason?: boolean;
};

const RejectionModal: React.FC<RejectionModalProps> = ({
  visible,
  setVisible,
  editingReason,
}) => {
  const { acceptReject, editReason, listingAgreement } =
    React.useContext(DocumentsContext);

  const getDefaultReason = () => {
    if (!editingReason) return '';
    return listingAgreement?.reason || '';
  };

  const [reason, setReason] = React.useState(getDefaultReason);
  const [error, setError] = React.useState<React.ReactNode>(false);

  const reject = () => {
    if (error) return;
    if (!reason) {
      return setError(true);
    }
    setVisible(false);
    if (editingReason) {
      return editReason(reason, true);
    }
    acceptReject('rejected', reason);
  };

  const save = () => {
    if (error) return;
    if (!reason) {
      return setError(true);
    }
    setVisible(false);
    editReason(reason, false);
  };

  React.useEffect(() => {
    if (reason.length > 200) {
      setError(
        <div>
          Reason must not exceed 200 characters.
          <div>(Current count: {reason.length})</div>
        </div>,
      );
    } else {
      setError(false);
    }
  }, [reason]);

  React.useEffect(() => {
    setReason(getDefaultReason());
  }, [visible]);

  return (
    <Modal
      open={visible}
      title="Send Rejection"
      width={400}
      onCancel={() => setVisible(false)}
      footer={
        <StyledFooter>
          <Button type="text" onClick={() => setVisible(false)}>
            Cancel
          </Button>
          {editingReason && <Button onClick={save}>Save</Button>}
          <Button
            type="default"
            danger
            onClick={reject}
            icon={
              <Icon
                type="antdMailOutlined"
                color="danger"
                width={12}
                offset={[0, -3]}
              />
            }
          >
            Send Rejection
          </Button>
        </StyledFooter>
      }
    >
      <InputTextArea
        label="Reason"
        placeholder="Include the reason why the listing paperwork has been rejected here..."
        value={reason}
        onChange={({ target: { value } }) => setReason(value)}
        autoSize={{ minRows: 5, maxRows: 20 }}
        error={error}
      />
    </Modal>
  );
};

const StyledFooter = styled.div`
  margin-right: 8px;
`;

export default RejectionModal;
